
import React, { useState } from 'react';
import { useAppContext } from '../App';
import { Star, Send, MessageSquare, CheckCircle, Loader2 } from 'lucide-react';
import { supabase } from '../services/supabaseClient';

const Contact: React.FC = () => {
    const { t, currentUser } = useAppContext();
    const [rating, setRating] = useState(0);
    const [hoverRating, setHoverRating] = useState(0);
    const [message, setMessage] = useState('');
    const [showSuccess, setShowSuccess] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (rating === 0) return;
        setIsSubmitting(true);

        // 1. Fire and forget: Save to Supabase (Non-blocking)
        supabase.from('feedback').insert({
             user_id: currentUser?.id || null,
             rating: rating,
             message: message
        }).then(({ error }) => {
            if (error) console.error("DB Save Error:", error);
        });

        // 2. Submit via AJAX to hide email from address bar
        try {
            const response = await fetch("https://formsubmit.co/holynessapp@gmail.com", {
                method: "POST",
                headers: { 
                    "Content-Type": "application/json",
                    "Accept": "application/json"
                },
                body: JSON.stringify({
                    _subject: "Holyness App Feedback",
                    _captcha: "false",
                    _template: "table",
                    email: currentUser?.email || "anonymous@holyness.app",
                    name: currentUser?.name || "Anonymous User",
                    role: currentUser?.role || "Guest",
                    rating_score: `${rating} Stars`,
                    message: message
                })
            });

            if (response.ok) {
                setShowSuccess(true);
                setRating(0);
                setMessage('');
            } else {
                console.error("Submission failed");
                alert(t('feedback_error') || "Failed to send feedback. Please try again.");
            }
        } catch (error) {
            console.error("Network error", error);
            alert(t('feedback_error') || "Failed to send feedback. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="max-w-2xl mx-auto space-y-8 py-8 animate-fadeIn">
            <div className="text-center space-y-2">
                <h1 className="text-3xl font-bold text-gray-900">{t('contact_title')}</h1>
                <p className="text-gray-500 max-w-lg mx-auto">{t('contact_subtitle')}</p>
            </div>

            <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
                <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-6 text-center text-white">
                    <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-80" />
                    <h2 className="text-xl font-bold">{t('rate_experience')}</h2>
                </div>

                <div className="p-8">
                    {showSuccess ? (
                        <div className="text-center py-8 animate-fadeIn">
                            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <CheckCircle className="w-8 h-8 text-green-600" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">{t('feedback_success')}</h3>
                            <p className="text-gray-500 mb-6">
                                Your message has been sent successfully.
                            </p>
                            <button 
                                onClick={() => setShowSuccess(false)}
                                className="text-blue-600 font-bold hover:underline"
                            >
                                Send another response
                            </button>
                        </div>
                    ) : (
                        <form 
                            onSubmit={handleSubmit}
                            className="space-y-6"
                        >
                            {/* --- UI --- */}
                            <div className="flex justify-center gap-2">
                                {[1, 2, 3, 4, 5].map((star) => (
                                    <button
                                        key={star}
                                        type="button"
                                        onClick={() => setRating(star)}
                                        onMouseEnter={() => setHoverRating(star)}
                                        onMouseLeave={() => setHoverRating(0)}
                                        className="focus:outline-none transition-transform hover:scale-110"
                                    >
                                        <Star 
                                            className={`w-10 h-10 ${
                                                star <= (hoverRating || rating) 
                                                ? 'fill-yellow-400 text-yellow-400' 
                                                : 'text-gray-300'
                                            }`} 
                                        />
                                    </button>
                                ))}
                            </div>
                            <p className="text-center text-sm text-gray-500 font-medium">
                                {rating > 0 ? `${rating} / 5 Stars` : t('rate_app')}
                            </p>

                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">
                                    {t('opinion_label')}
                                </label>
                                <textarea 
                                    name="message"
                                    required
                                    className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition h-32 resize-none"
                                    placeholder={t('opinion_placeholder')}
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                />
                            </div>

                            <button 
                                type="submit" 
                                disabled={rating === 0 || isSubmitting}
                                className="w-full bg-blue-600 text-white py-3 rounded-lg font-bold hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                {isSubmitting ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" /> Sending...
                                    </>
                                ) : (
                                    <>
                                        <Send className="w-4 h-4" /> {t('submit_feedback')}
                                    </>
                                )}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Contact;
